<template>
    <div class="timeline-event">

        <div
            :class="[ isComment || isExternalComment || isTimelineEvent || isDataExtension && event.action !== 'applications.unrejected' ?
            ( isRejected ? 'text-semantic-error' : 'text-primary') : 'text-secondary',
            (isExternalComment ? 'rounded-6 border border-semantic-info bg-semantic-info-light p-16' : '')]"
            class="mb-12"
        >

            <div class="flex items-start">
                <avatar v-if="(isComment || isRejectedWithReason || isHiredWithReason) && event.user"
                        :image="event.user.image_url"
                        size="small"
                        class="mr-8">
                    {{ event.user.initials }}
                </avatar>

                <div :class="{'border border-primary-5 bg-primary-5 rounded-6 p-16': isComment,
                               'border border-semantic-error-light bg-semantic-error-light rounded-6 p-16': isRejectedWithReason,
                               'border border-secondary-40 bg-secondary-5 rounded-6 p-16': isHiredWithReason,}"
                     class="flex items-start w-full">

                    <div class="mr-12 flex items-center" style="height: 2.1rem;">
                        <svg-icon :type="icon" class="max-w-16 max-h-16"></svg-icon>
                    </div>

                    <div :class="isComment ? 'w-full' : ''">
                        <div class="first-letter:uppercase" :class="isComment ? 'flex' : ''">

                            <span v-if="isTimelineEvent">

                                <span v-if="event.action === 'applications.stage_moved'">
                                    <span>{{ $t('timeline.moved_to_stage') }}</span>
                                    <!-- We check for stage title translations here, as stages used to be translated, and we still have timeline events where those translations are present. -->
                                    <span class="font-semibold italic">{{ event.data.stage.title[locale] || event.data.stage.title }}</span>
                                </span>

                                <span v-else-if="event.action === 'applications.updated'">
                                    <span>{{ $t('timeline.data_updated') }}</span>
                                </span>

                                <span v-else-if="event.action === 'applications.added_to_officient'">
                                    <span>{{ $t('timeline.added_to_officient') }}</span>
                                </span>

                                <span v-else-if="event.action === 'applications.score_updated'">
                                    <span>{{ $t('timeline.score_updated') }}</span>
                                </span>

                                <span v-else-if="event.action === 'interviews.created'">
                                    <span v-html="$t('timeline.interview_created', {interview_name: interview ? interview.name : ''})"></span>
                                </span>

                                <span v-else-if="event.action === 'interviews.updated'">
                                    <a class="hover:text-secondary transition duration-50 cursor-pointer" @click="openInterview(event.data.interview.id)">
                                        <template v-if="interviewStartsAt(event.data.interview)">
                                            {{ $t('timeline.interview_updated', {date: interviewStartsAt(event.data.interview).locale(locale).format(dateFormat)}) }}
                                        </template>
                                        <template v-else>
                                            <span>{{ $t('timeline.interview_updated_without_date') }}</span>
                                        </template>
                                    </a>
                                </span>

                                <span v-else-if="event.action === 'interviews.rescheduled'">
                                    <span v-if="event.data">
                                        <span v-if="event.data.interview.status && event.data.interview.status.id === 4">
                                            {{ $t('timeline.interview_rescheduled') }}
                                        </span>
                                        <span v-if="event.data.interview.status && event.data.interview.status.id === 5">
                                            {{ $t('timeline.interview_rescheduled_by', {user: event.data.interview.rescheduled_by ? event.data.interview.rescheduled_by.name : ''}) }}
                                        </span>
                                    </span>
                                </span>

                                <span v-else-if="event.action === 'interviews.canceled'">
                                    <span v-if="event.data">
                                        <span v-if="event.data.interview.status && event.data.interview.status.id === 6">
                                            {{ $t('timeline.interview_canceled') }}
                                        </span>
                                        <span v-if="event.data.interview.status && event.data.interview.status.id === 7">
                                            {{ $t('timeline.interview_cancelled_by', {user: event.data.interview.canceled_by.name}) }}
                                        </span>
                                    </span>
                                </span>

                                <span v-else-if="event.action === 'interviews.deleted'">
                                    <span>{{ getDeletedMessage(event.data.interview) }}</span>
                                </span>

                                <span v-else-if="event.action === 'applications.job_moved'">
                                    <span>{{ $t('timeline.moved_to_job') }}</span>
                                    <span class="font-semibold italic">{{ jobName }}</span>
                                </span>

                                <span v-else-if="isRejected">
                                    <span>{{ $t('timeline.rejected') }}</span>
                                </span>

                                <span v-else-if="event.action === 'applications.unrejected'">
                                    <span>{{ $t('timeline.unrejected') }}</span>
                                </span>

                                <span v-else-if="event.action === 'contacts.created'">
                                    <span v-if="event.eventable && !event.eventable.sent && event.eventable.scheduled_at">
                                        {{ $t('timeline.email_planned_on') }}
                                        <span class="font-semibold">{{ formatDate(event.eventable.scheduled_at) }}</span>
                                    </span>
                                    <span v-else>{{ $t('timeline.contact_created') }}</span>
                                </span>

                                <span v-else-if="event.action === 'retention-extensions.created'">
                                    <span>{{ $t('timeline.retention_extension_created') }}</span>
                                </span>

                                <span v-else-if="event.action === 'inbound-emails.created'">
                                    <span>{{ $t('timeline.inbound_created') }}</span>
                                </span>

                                <span v-else-if="event.action === 'invitations.created'">
                                    <span>{{ $t('timeline.invitation_created') }}</span>
                                </span>

                                <span v-else-if="event.action === 'interviews.confirmed' || event.action === 'interviewInvitation.confirmed'">
                                    <span>{{ $t('timeline.interviewInvitation_confirmed', {date: formatDate(event.data.confirmed_time)}) }}</span>
                                </span>

                                <span v-else-if="event.action === 'documents.created'">
                                    <span>{{ $t('general.doc') }}</span>
                                    <span class="font-semibold italic">"{{ event.data.document.original_name }}"</span>
                                    <span>{{ $t('timeline.added') }}</span>
                                </span>

                                <span v-else-if="event.action === 'documents.deleted'">
                                    <span>{{ $t('general.doc') }}</span>
                                    <span class="font-semibold italic">"{{ event.data.document.original_name }}"</span>
                                    <span>{{ $t('timeline.deleted') }}</span>
                                </span>

                                <span v-else-if="event.action === 'application.tasks.created'">
                                    <span>{{ $t('timeline.task') }}</span>
                                    <span class="font-semibold italic">"{{ event.data.task.title }}"</span>
                                    <span>{{ $t('timeline.added') }}</span>
                                </span>

                                <span v-else-if="event.action === 'application.tasks.completed'">
                                    <span>{{ $t('timeline.task') }}</span>
                                    <span class="font-semibold italic">"{{ event.data.task.title }}"</span>
                                    <span>{{ $t('timeline.completed') }}</span>
                                </span>

                                <span v-else-if="event.action === 'application.tasks.deleted'">
                                    <span>{{ $t('timeline.task') }}</span>
                                    <span class="font-semibold italic">"{{ event.data.task.title }}"</span>
                                    <span>{{ $t('timeline.deleted') }}</span>
                                </span>

                                <span v-else>
                                    {{ event }}
                                </span>

                                <span v-if="event.data.user">
                                    <span> {{ $t('general.by') }}</span>
                                    <span class="font-semibold">{{ byMe ? $t('timeline.you') : event.data.user.name }}</span>
                                </span>

                                <span v-if="event.action === 'inbound-emails.created'">
                                    <span> {{ $t('general.from') }}</span>
                                    <span class="font-semibold">{{ application.first_name }}</span>
                                </span>
                            </span>

                            <span v-else-if="isComment" class="inline-block first-letter:uppercase">
                                <span v-if="byMe">
                                    <span class="font-semibold">{{ $t('general.you') }}</span>
                                    <span class="text-primary-80">{{ $t('timeline.commented_you') }}</span>
                                </span>
                                <span v-else-if="event.user">
                                    <span class="font-semibold">{{ event.user.name }}</span>
                                    <span class="text-primary-80">{{ $t('timeline.commented_other') }}</span>
                                </span>
                                <span v-else>
                                    <span class="font-semibold">System</span>
                                    <span class="text-primary-80">{{ $t('timeline.commented_other') }}</span>
                                </span>
                            </span>

                            <span v-else-if="isExternalComment">
                                <span v-if="!byExternal">
                                    <span class="font-semibold">{{ externalCommentInternUser }}</span>
                                    <span class="text-primary-80">{{ $t('timeline.commented_you') }}</span>
                                </span>
                                <span v-else>
                                    <span class="font-semibold inline-flex">
                                        <svg-icon class="h-16 w-16 mr-6" type="location-city"></svg-icon>
                                        {{ externalCommentExternalUser }}
                                    </span>
                                    <span class="text-primary-80">{{ $t('timeline.commented_other') }}</span>
                                </span>
                            </span>

                            <span v-else>
                                <span v-if="isDataExtension">
                                    {{ $t('timeline.retention_extension_accepted') }}
                                </span>
                                <span v-else-if="!event.source">
                                    <template v-if="originalJobName">
                                        <div v-if="application.referrer_source === 'interim' && application.interim" class="inline-block">
                                            {{ $t('timeline.application_received_interim', {applicant: application.first_name + ' ' + application.last_name, interim: application.interim.name}) }}
                                        </div>
                                        <div v-else class="inline-block">
                                            {{ $t('timeline.application_received_with_job', {job: originalJobName}) }}
                                        </div>
                                    </template>
                                    <template v-else>
                                        {{ $t('timeline.application_received') }}
                                    </template>
                                </span>
                                <span v-else>
                                    {{ event.source }}
                                </span>
                            </span>

                            <tooltip :content="createdAt" class="inline-flex" offset="0,4" position="top"
                                     :class="isComment ? 'ml-4' : ''">
                                <span class="cursor-default text-primary-80">• {{ timeAgo(event.created_at) }}</span>
                            </tooltip>

                            <div v-if="isComment && byMe" class="flex-grow inline-flex items-center justify-end">
                                <button
                                    class="leading-none flex justify-center items-center text-center trans-ease text-primary-40 hover:text-primary"
                                    @click="editComment(event)"
                                >
                                    <svg-icon class="max-w-16 max-h-16 relative z-10" type="edit"/>
                                </button>
                                <button
                                    :class="[ deletingComment === event ? 'text-semantic-error' : 'text-primary-40' ]"
                                    class="ml-8 leading-none flex justify-center items-center text-center trans-ease hover:text-semantic-error"
                                    @click="deleteComment(event)"
                                >
                                    <svg-icon class="max-w-16 max-h-16 relative z-10" type="delete"/>
                                </button>
                            </div>
                        </div>

                        <div v-if="isComment">
                            <span class="text-primary-40 flex items-center my-4">
                                <svg-icon class="max-w-12 max-h-12 mr-6" type="visibility"></svg-icon>
                                <span v-if="event.visibility === 'all'">{{ $t('timeline.only_visible_for_team') }}</span>
                                <span v-if="event.visibility === 'selected'">
                                    {{ $t('timeline.only_visible_for') }}
                                    <user-avatars size="small" :users="commentUsers" display="inline-flex"/>
                                </span>
                                <span v-if="event.visibility === 'me'">{{ $t('timeline.only_visible_for_me') }}</span>
                            </span>
                            <div v-html="event.text" v-if="!editingComment"></div>
                            <comment-box
                                v-else :application="application"
                                :comment="editingComment"
                                slim
                                @cancel="editingComment = null"
                            />
                        </div>

                        <div v-if="isExternalComment"
                             class="mt-12"
                             v-html="event.comment">
                        </div>

                        <div v-if="isRejectedWithReason" class="text-semantic-error mt-12">
                            <b>{{ event.data.reason }}</b>
                            <p>{{ event.data.comment }}</p>

                            <div class="flex items-center mt-4">
                                <svg-icon class="h-16 w-16 mr-6" type="location-city"></svg-icon>
                                <p>{{ event.data.comment_external }}</p>
                            </div>
                        </div>

                        <div v-if="isHiredWithReason" class="text-secondary-80 mt-12">
                            <div class="flex items-center mt-4">
                                <svg-icon class="h-16 w-16 mr-6" type="location-city"></svg-icon>
                                <p>{{ event.data.hire_comment_external }}</p>
                            </div>
                        </div>

                        <div v-if="(isRescheduled || isCancelled) && (rescheduledByInterviewee || cancelledByInterviewee)"
                             class="pt-16 mb-8 flex items-center"
                        >
                            <div class="rounded-32 h-20 w-8 mr-8 bg-primary"></div>
                            <a class="leading-none font-semibold underline trans-ease cursor-pointer flex items-center hover:text-primary"
                               @click="showStatusReason"
                            >
                                <div class="mr-4">
                                    <span>{{ statusTitle }}</span>
                                </div>
                            </a>
                        </div>

                        <div v-if="isEmail" class="pt-16 mb-8 flex items-center">
                            <div
                                v-if="event.eventable.hasOwnProperty('sent')"
                                :class="[ emailStatusColor ]"
                                class="rounded-32 h-20 w-8 mr-8"
                            ></div>
                            <a :class="emailStatusColorText" class="leading-none font-semibold underline trans-ease cursor-pointer flex items-center"
                               @click="showEmailDetail(event.eventable)">
                                <div class="mr-4">
                                    {{ event.eventable.subject || $t('timeline.no_subject') }}
                                </div>
                                <svg-icon v-if="documents.length"
                                          class="max-w-16 max-h-16 text-primary"
                                          type="attach-file"></svg-icon>
                            </a>
                        </div>

                        <div v-if="event.action === 'interviews.created' && interview">
                            <div class="pt-16 mb-8 leading-none">
                                <div class="flex items-center">
                                    <div class="font-semibold flex-grow">
                                        <div class="flex items-center mb-8">
                                            <svg-icon
                                                class="max-w-16 max-h-16 fill-primary-40 mr-8"
                                                :type="Number(interviewers.length) > 1 ? 'groups' : 'person'"
                                            />
                                            <span>
                                                {{ interviewerNames }}
                                            </span>
                                        </div>
                                        <a :href="interviewLink" class="block flex items-center mb-8" target="_blank">
                                            <svg-icon class="max-w-16 max-h-16 text-primary-40 mr-8"
                                                      type="location-on"></svg-icon>
                                            <span class="break-all">{{
                                                    interview ? interview.location : event.data.interview.location
                                                }}</span>
                                        </a>
                                        <div v-if="interview ? !interview.starts_at : !event.data.interview.time"
                                             class="flex items-center mb-8">
                                            <svg-icon class="max-w-16 max-h-16 text-primary-40 mr-8"
                                                      type="schedule"></svg-icon>
                                            <span>{{ $t('interview.waiting_for_confirmation') }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="event.action === 'interviews.confirmed'">
                            <div class="pt-16 mb-8 leading-none">
                                <div class="flex items-center">
                                    <div v-if="interviewStartsAt(interview)"
                                         class="border border-primary-20 rounded-6 overflow-hidden flex flex-col items-center w-64 mr-16 shrink-0">
                                        <div class="font-semibold uppercase text-center py-8 text-white bg-primary text-body-sm w-full">
                                            {{ interview.timeMoment.locale(locale).format('MMM').replace('.', '') }}
                                        </div>
                                        <div class="font-semibold text-heading-1 py-8">
                                            {{ interview.timeMoment.locale(locale).format('DD') }}
                                        </div>
                                        <div class="font-semibold border-t border-primary-20 text-body-sm py-8">
                                            {{ interview.timeMoment.locale(locale).format('HH:mm') }}
                                        </div>
                                    </div>

                                    <div class="font-semibold flex-grow">
                                        <div class="flex items-center mb-8">
                                            <svg-icon
                                                class="max-w-16 max-h-16 text-primary-40 mr-8"
                                                :type="Number(interviewers.length) > 1 ? 'groups' : 'person'"
                                            />
                                            <span>
                                                {{ interviewerNames }}
                                            </span>
                                        </div>
                                        <a :href="interviewLink" class="block flex items-center mb-8" target="_blank">
                                            <svg-icon class="max-w-16 max-h-16 text-primary-40 mr-8"
                                                      type="location-on"></svg-icon>
                                            <span class="break-all">{{
                                                    interview ? interview.location : event.data.interview.location
                                                }}</span>
                                        </a>
                                        <div v-if="interview ? !interview.starts_at : !event.data.confirmed_time"
                                             class="flex items-center mb-8">
                                            <svg-icon class="max-w-16 max-h-16 text-primary-40 mr-8"
                                                      type="schedule"></svg-icon>
                                            <span>{{ $t('interview.waiting_for_confirmation') }}</span>
                                        </div>
                                        <div v-if="interview ? interview.starts_at : false">
                                            <div class="add-to-calendar -mb-8" title="Add to your calendar">
                                                <span class="start">{{
                                                        interviewStartsAt(interview).format('L hh:mm A')
                                                    }}</span>
                                                <span class="timezone">{{
                                                        Intl.DateTimeFormat().resolvedOptions().timeZone
                                                    }}</span>
                                                <span class="end">{{
                                                        interviewStartsAt(interview).add(interview.duration, 'minutes').format('L hh:mm A')
                                                    }}</span>
                                                <span class="duration">{{ interview.duration }}</span>
                                                <span class="title">{{
                                                        $t('interview.add_to_your_calendar.title_company', {candidate: application.first_name + ' ' + application.last_name})
                                                    }}</span>
                                                <span class="description">{{
                                                        $t('interview.add_to_your_calendar.title', {job: interview.user.name})
                                                    }}</span>
                                                <span class="location">{{ interview.location }}</span>
                                                <span class="label">{{
                                                        $t('interview.add_to_your_calendar.label')
                                                    }}</span>
                                            </div>
                                            <!-- <a :href="invitationInterview.interview.icsFilepath"-->
                                            <!-- class="inline-flex items-center bg-secondary hover:bg-secondary-80 trans-ease px-8 py-4 text-body-sm text-white rounded-6 shadow-sm" download>-->
                                            <!-- <span>{{ $t('interview.download_ics') }}</span>-->
                                            <!-- </a>-->
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <popup-modal
            v-if="showingStatusReason"
            :title="statusTitle"
            position="center"
            @close="closeStatusReason"
        >
            <div class="w-768">
                <div class="px-16 border-b-2 border-primary-20">
                    <p class="text-body whitespace-pre-wrap leading-relaxed mb-24" v-html="showingStatusReason"></p>
                </div>
            </div>
        </popup-modal>

        <popup-modal
            v-if="showingEmailDetail"
            :title="showingEmailDetail.subject"
            position="center"
            @close="closeEmailDetail"
        >
            <div class="w-768">
                <div class="flex border-b-2 border-primary-20 justify-between -mt-32 pt-24">
                    <div class="mb-24 w-full">
                        <banner
                            class="mb-20"
                            :description="$t('applications.mails_not_sent_in_free_trial')"
                            warning
                            v-if="!currentCompany.is_demo && currentCompany.in_trial"
                        />
                        <div v-if="showingEmailDetail.moment" class="flex items-center flex-none mb-16">
                            <svg-icon class="max-w-16 max-h-16 mr-8 text-secondary" type="check-circle"></svg-icon>
                            <span class="text-primary-80">{{ showingEmailDetail.moment.locale(locale).format(datetimeFormat) }}</span>
                        </div>
                        <div v-else-if="showingEmailDetail.failed" class="flex items-center flex-none mb-16">
                            <banner
                              :title="$t('mailer.problem_sending_mail')"
                              :description="showingEmailDetail.failed_reason"
                              class="w-full"
                              :one-line="false"
                              inline
                              danger
                            />
                        </div>
                        <div v-else-if="showingEmailDetail.scheduled_at" class="flex items-center flex-none mb-16">
                            <svg-icon class="max-w-16 max-h-16 mr-8 text-primary" type="schedule"></svg-icon>
                            <span class="text-primary-80 mr-4">
                                {{ $t('mailer.planned_on') }} {{ showingEmailDetail.scheduledMoment.locale(locale).format(datetimeFormat) }}.
                            </span>
                            <span class="text-primary underline cursor-pointer trans-ease hover:text-primary-60"
                                  @click="startEditingMail(showingEmailDetail)">{{ $t('mailer.edit_planned_mail') }}
                            </span>
                        </div>
                        <div v-else class="flex items-center flex-none mb-16">
                            <svg-icon class="max-w-16 max-h-16 mr-8 text-primary" type="schedule"></svg-icon>
                            <span class="text-primary-80 mr-4">
                                {{ $t('mailer.mail_being_sent') }}
                            </span>
                        </div>
                        <div class="flex items-start">
                            <div class="mr-8 capitalize">
                                {{ $t('general.from') }}:
                            </div>
                            <div class="font-semibold">
                                {{ showingEmailDetail.from }}
                            </div>
                        </div>
                        <div class="flex items-start pt-4">
                            <div class="mr-8 capitalize">
                                {{ $t('mailer.to') }}:
                            </div>
                            <div class="font-semibold">
                                {{ _castArray(showingEmailDetail.to).join(', ') }}
                            </div>
                        </div>
                        <div v-if="showingEmailDetail.hasOwnProperty('cc') && _castArray(showingEmailDetail.cc).length"
                             class="flex items-start pt-4">
                            <div class="mr-8 capitalize">
                                Cc:
                            </div>
                            <div class="font-semibold">
                                {{ _castArray(showingEmailDetail.cc).join(', ') }}
                            </div>
                        </div>
                        <div v-if="showingEmailDetail.hasOwnProperty('bcc') && _castArray(showingEmailDetail.bcc).length"
                             class="flex items-start pt-4">
                            <div class="mr-8 capitalize">
                                Bcc:
                            </div>
                            <div class="font-semibold">
                                {{ _castArray(showingEmailDetail.bcc).join(', ') }}
                            </div>
                        </div>
                        <ul v-if="documents.length" class="w-full flex items-center flex-wrap pt-12">
                            <li
                                v-for="document in documents"
                                :key="'email-document-' + document.id"
                                class="mr-12 pt-4"
                            >
                                <a
                                    :href="document.document_url"
                                    class="flex items-center hover:text-primary trans-ease"
                                    target="_blank"
                                >
                                    <svg-icon class="max-w-16 max-h-16 text-primary mr-4" type="attach-file"></svg-icon>
                                    <span class="font-semibold underline">{{ document.original_name || document.filename }}</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="px-16 pt-24 border-b-2 border-primary-20">
                    <p class="text-body whitespace-pre-wrap leading-relaxed mb-24" v-html="showingEmailDetail.message"></p>
                </div>

                <reply-to-mail
                    :applications="[application]"
                    :default-subject="'Re: ' + showingEmailDetail.subject"
                    :total-applications="1"
                    class="pt-24"
                    @close="closeEmailDetail"
                />
            </div>
        </popup-modal>
        <mail-creator
            v-if="!!editingMail"
            context="single"
            :applications="[application]"
            :contact="editingMail"
            :documents="documents"
            :total-applications="1"
            @close="stopEditingMail"
        />
    </div>
</template>

<script>
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useAuthenticationStore} from '../state/modules/authentication';

import moment from 'moment';
import TimelineEvent from '../models/TimelineEvent';
import Comment from '../models/Comment';
import InterimComment from '../models/InterimComment';
import SvgIcon from './ui/SvgIcon.vue';
import Tooltip from './navigation/Tooltip.vue';
import Loading from './ui/Loading.vue';
import * as comments from '../services/CommentService';
import PopupModal from './ui/PopupModal.vue';
import Job from '../models/Job';
import Application from '../models/Application';
import _castArray from 'lodash/castArray';
import Document from '../models/Document';
import EventBus from '../../shared/eventbus';
import ReplyToMail from './ui/ReplyToMail';
import InboundEmail from '../models/InboundEmail';
import MailCreator from './ui/MailCreator';
import Avatar from './ui/Avatar';
import UserAvatars from './ui/UserAvatars';
import User from '../models/User';
import '../../shared/add-to-calendar';
import '../../shared/add-to-calendar.css';
import DataRetentionExtensionRequest from '../models/DataRetentionExtensionRequest';
import CommentBox from './ui/CommentBox';
import Interview from "../models/Interview";
import Banner from "./ui/Banner.vue";

export default {
    components: {
      Banner,
        CommentBox,
        UserAvatars,
        Avatar,
        MailCreator,
        'svg-icon': SvgIcon,
        'tooltip': Tooltip,
        'loading': Loading,
        'popup-modal': PopupModal,
        'reply-to-mail': ReplyToMail,
    },
    props: {
        event: {
            type: Object,
            required: true,
        },
        application: {
            type: Application,
            required: true,
        },
    },
    data: function () {
        return {
            // oneWeekAgo: moment().subtract(1, 'w'),
            // oneYearAgo: moment().subtract(1, 'y'),
            editingComment: null,
            deletingComment: null,
            showingStatusReason: null,
            showingEmailDetail: null,
            editingMail: null,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale', 'dateFormat', 'datetimeFormat']),
        ...mapState(useAuthenticationStore, ['currentUser', 'currentUserId']),
        documents() {

            if (!this.isEmail) {
                return [];
            }

            return useRepo(Document)
                .where('attachable_id', this.event.eventable.id)
                .get();
        },
        isComment() {
            return this.event instanceof Comment;
        },
        isExternalComment() {
            return this.event instanceof InterimComment;
        },
        isTimelineEvent() {
            return this.event instanceof TimelineEvent;
        },
        isEmail() {
            return this.isTimelineEvent && ['contacts.created', 'invitations.created', 'inbound-emails.created', 'retention-extensions.created', 'interview-canceleds.created', 'interview-updateds.created'].includes(this.event.action);
        },
        isRescheduled() {
            return this.isTimelineEvent && this.event.action === 'interviews.rescheduled';
        },
        isCancelled() {
            return this.isTimelineEvent && this.event.action === 'interviews.canceled';
        },
        hasStage() {
            return this.isTimelineEvent && ['applications.stage_moved', 'applications.job_moved'].includes(this.event.action);
        },
        jobName() {
            if (!this.hasStage) {
                return null;
            }

            if (this.event.data.job.id) {
                let job = useRepo(Job).whereId(this.event.data.job.id).first();

                return this.translated(job.title, this.locale, true);
            }

            return null;
        },
        interview() {
            return useRepo(Interview)
                .with('user')
                .where('id', this.event.eventable_id)
                .first();
        },
        interviewLink() {
            if (!['zoom', 'google', 'ms-teams', 'online'].includes(this.interview?.type)) {
                return null;
            }

            return this.interview.meeting_url;
        },
        interviewName() {
            if (this.event.data.interview) {
                return this.event.data.interview.name;
            } else if (this.interview) {
                return this.interview.name;
            } else {
                return 'interview';
            }
        },
        createdAt() {
            return moment(this.event.created_at).locale(this.locale).format(this.datetimeFormat);
        },
        byMe() {
            return this.event.user_id && this.event.user_id == this.currentUserId;
        },
        byExternal() {
            //External comments are send by company not by user
            return this.event.interim_user_id && this.event.interim_user_id > 0;
        },
        icon() {

            if (this.isTimelineEvent) {

                if (this.event.action === 'applications.added_to_officient') {
                    return 'officient-color';
                }

                if (['applications.stage_moved', 'applications.job_moved'].includes(this.event.action)) {
                    return 'keyboard-arrow-right';
                }

                if (this.isRejected) {
                    return 'front-hand';
                }

                if (this.event.action === 'applications.score_updated') {
                    return 'star';
                }


                if (/^contacts\.created|invitations\.created$/.test(this.event.action)) {
                    return 'forward-to-inbox';
                }

                if (this.event.action === 'inbound-emails.created') {
                    return 'mail';
                }

                if (/^interviews\.[a-z_]+$/.test(this.event.action)) {
                    return 'calendar-today';
                }

                if (/^documents\.[a-z_]+$/.test(this.event.action)) {
                    return 'attach-file';
                }

                if (this.event.action === 'applications.updated') {
                    return 'edit';
                }

                if (/^application\.tasks\.[a-z_]+$/.test(this.event.action)) {
                    return 'task-alt';
                }

                if (this.event.action === 'retention-extensions.created') {
                    return 'gdpr';
                }

                return 'check-circle';
            }

            if (this.isComment) {
                return 'forum';
            }
            if (this.isExternalComment) {
                return 'forum-filled';
            }

            if (this.isDataExtension) {
                return 'gdpr'
            }

            return 'accessibility';
        },
        originalJobName() {
            if (!this.event.original_stage) {
                return;
            }

            if (!this.event.original_stage.job) {
                return;
            }

            return this.translated(this.event.original_stage.job.title, this.currentUser.language, true);
        },
        emailStatusColor() {
            const mail = this.event.eventable;

            if (mail.sent) {
                return 'bg-secondary';
            }

            if (mail.failed) {
                return 'bg-semantic-error';
            }

            return 'bg-primary';
        },
        emailStatusColorText() {
            const mail = this.event.eventable;

            if (mail.sent) {
                return 'hover:text-secondary';
            }

            if (mail.failed) {
                return 'hover:text-semantic-error';
            }

            return 'hover:text-primary';
        },
        isRejected() {
            return this.event.action === 'applications.rejected';
        },
        isRejectedWithReason() {
            return this.isRejected && (this.event.data.reason || this.event.data.comment || this.event.data.comment_external);
        },
        isHired() {
            return !!this.application.stage.onboarding;
        },
        isHiredWithReason() {
            return this.event?.data?.hire_comment_external;
        },
        commentUsers() {
            return useRepo(User).whereIn('id', this.event.users).get();
        },
        isDataExtension() {
            return this.event instanceof DataRetentionExtensionRequest;
        },
        externalCommentInternUser() {
            let name = '';
            if (this.event.user?.id === this.currentUser.id) {
                name += 'You - ';
            } else if (this.event.user) {
                name += `${this.event.user.first_name} ${this.event.user.last_name} - `;
            }
            name += this.currentCompany.name
            return name;
        },
        externalCommentExternalUser() {
            let name = '';
            if (this.event.interim_user) {
                name += `${this.event.interim_user.name} - `;
            }
            name += this.application.interim.name
            return name;
        },
        statusTitle() {
            if (this.rescheduledByInterviewee) {
                return this.$t('interview.show_reschedule_reason_title');
            } else if (this.cancelledByInterviewee) {
                return this.$t('interview.show_cancel_reason_title');
            } else {
                return '';
            }
        },
        rescheduledByInterviewee() {
            // STATUS_RESCHEDULE_BY_INTERVIEWEE (5)
            return this.event.data.interview.status.id === 5;
        },
        cancelledByInterviewee() {
            // STATUS_CANCELED_BY_INTERVIEWEE (7)
            return this.event.data.interview.status.id === 7;
        },
        interviewers() {
            let requiredInterviewers = [];
            let interviewers = this.event.data.interviewers ?? [this.event.data.interviewer] ?? [];
            // iterate over object interviewers
            for(let key in interviewers) {
                let interviewer = interviewers[key];
                if (interviewer.hasOwnProperty('required') && !!interviewer.required) {
                    requiredInterviewers.push(interviewer);
                }
            }

            if (requiredInterviewers.length > 0) {
                return requiredInterviewers;
            } else {
                return interviewers;
            }
        },
        interviewerNames() {
            let names = '';
            this.interviewers.forEach((interviewer, index) => {
                names += interviewer.name + (index < this.interviewers.length - 1 ? ', ' : '');
            });
            return names;
        }
    },
    methods: {
        moment,
        _castArray,
        editComment(comment) {
            this.editingComment = comment;
        },
        deleteComment(comment) {
            this.deletingComment = comment;

            comments.destroy(comment.id).then(() => {
                useRepo(Comment).destroy(comment.id);
                this.deletingComment = null;
            });
        },
        showEmailDetail(email) {
            email.message = email.message.replace(/<p><\/p>/g, '<br/>');
            this.showingEmailDetail = email;
        },
        closeStatusReason() {
            this.showingStatusReason = null;
        },
        closeEmailDetail() {
            this.showingEmailDetail = null;
        },
        formatDate(date, format = 'LLLL') {
            return moment.utc(date)
                .local()
                .locale(this.locale)
                .format(format);
        },
        openInterview(id) {
            if (!id) return;

            bus.dispatch('open_interview', id);
        },
        checkInboundInstance() {
            return this.showingEmailDetail instanceof InboundEmail;
        },
        startEditingMail(mail) {
            this.editingMail = mail;
        },
        stopEditingMail(closePopup = false) {
            this.editingMail = null;

            if (closePopup) {
                this.showingEmailDetail = false;
            }
        },
        interviewStartsAt(interview) {
            return interview ? interview.starts_at ? moment(interview.starts_at) : interview.time ? moment(interview.time) : null : null;
        },
        getDeletedMessage(interview) {
            if (interview.starts_at || interview.time) return this.$t('timeline.interview_deleted', {date: this.formatDate(this.interviewStartsAt(interview))});
            else if (interview.name) return this.$t('timeline.interview.deleted.name', {name: interview.name});
            else return this.$t('timeline.interview_deleted', {date: this.formatDate(moment(interview.created_at))});
        },
        showStatusReason() {
            if (this.rescheduledByInterviewee) {
                this.showingStatusReason =
                    this.event.data.interview.rescheduled_by.message ?? this.interview.interviewee_reschedule_reason;
            } else if (this.cancelledByInterviewee) {
                this.showingStatusReason =
                    this.event.data.interview.canceled_by.message ?? this.interview.interviewee_cancel_reason;
            }
        },
    },
    watch: {
        'event.eventable': {
            deep: true,
            handler(val) {
                if (this.showingEmailDetail) {
                    val.message = val.message.replace(/<p><\/p>/g, '<br/>');
                    this.showingEmailDetail = val;
                }
            },
        },
    },
};
</script>

<style>
.timeline-event {

&
ul {
    @apply list-disc pl-20;
}

&
ol {
    @apply list-decimal pl-20;
}

}
</style>
